import React, { useEffect, useState } from 'react';
import { featuresApi, FeatureDefinition, FEATURE_CATEGORIES } from '../../lib/api/features';
import {
    Plus, Settings, Edit2, Trash2, Save, X,
    Hash, ToggleLeft, List, Calendar,
    LayoutDashboard, Layout, BarChart2, LayoutTemplate, Plug, BookOpen, Share2,
    RefreshCw, AlertTriangle
} from 'lucide-react';

// Icon map for categories
const CategoryIcon: Record<string, React.FC<any>> = {
    'board_explorer': LayoutDashboard,
    'board': Layout,
    'charts': BarChart2,
    'templates': LayoutTemplate,
    'channels': Plug,
    'journals': BookOpen,
    'sharing': Share2,
    'other': Settings,
};

// Icon for feature types
const TypeIcon: Record<string, React.FC<any>> = {
    'BOOLEAN': ToggleLeft,
    'INTEGER': Hash,
    'STRING_ARRAY': List,
    'DATE_RANGE_DAYS': Calendar,
};

interface FeatureFormData {
    key: string;
    name: string;
    description: string;
    category: string;
    valueType: string;
    defaultValue: any;
    usageModel: string;
    usageCountField: string;
}

const emptyForm: FeatureFormData = {
    key: '',
    name: '',
    description: '',
    category: 'other',
    valueType: 'BOOLEAN',
    defaultValue: false,
    usageModel: '',
    usageCountField: '',
};

export const FeatureRegistryPage: React.FC = () => {
    const [features, setFeatures] = useState<FeatureDefinition[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [formData, setFormData] = useState<FeatureFormData>(emptyForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filterCategory, setFilterCategory] = useState<string>('all');

    useEffect(() => {
        loadFeatures();
    }, []);

    const loadFeatures = async () => {
        try {
            setLoading(true);
            const data = await featuresApi.list();
            setFeatures(data);
        } catch (err) {
            console.error('Failed to load features:', err);
            setError('Unable to load features. Please refresh the page.');
        } finally {
            setLoading(false);
        }
    };

    const parseDefaultValue = (feature: FeatureDefinition) => {
        try {
            return JSON.parse(feature.defaultValue).value;
        } catch {
            return null;
        }
    };

    const formatDefaultValue = (value: any, type: string): string => {
        if (value === null || value === undefined) return '—';
        if (type === 'BOOLEAN') return value ? 'Yes' : 'No';
        if (type === 'INTEGER') return value === -1 ? 'Unlimited' : String(value);
        if (type === 'STRING_ARRAY') {
            if (Array.isArray(value)) {
                if (value.includes('*')) return 'All';
                return value.length > 3 ? `${value.slice(0, 3).join(', ')}...` : value.join(', ');
            }
        }
        return String(value);
    };

    const startEdit = (feature: FeatureDefinition) => {
        setEditingId(feature.id);
        setIsCreating(false);
        setFormData({
            key: feature.key,
            name: feature.name,
            description: feature.description || '',
            category: feature.category,
            valueType: feature.valueType,
            defaultValue: parseDefaultValue(feature),
            usageModel: feature.usageModel || '',
            usageCountField: feature.usageCountField || '',
        });
    };

    const startCreate = () => {
        setIsCreating(true);
        setEditingId(null);
        setFormData(emptyForm);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setIsCreating(false);
        setFormData(emptyForm);
        setError(null);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);

            const data = {
                ...formData,
                defaultValue: formData.defaultValue,
            };

            if (isCreating) {
                await featuresApi.create(data as any);
            } else if (editingId) {
                await featuresApi.update(editingId, data as any);
            }

            await loadFeatures();
            cancelEdit();
        } catch (err: any) {
            console.error('Failed to save feature:', err);
            setError('Unable to save feature. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this feature?')) return;

        try {
            await featuresApi.delete(id);
            await loadFeatures();
        } catch (err: any) {
            setError('Unable to delete feature. Please try again.');
        }
    };

    // Group features by category
    const groupedFeatures = features.reduce((acc, feature) => {
        const cat = feature.category || 'other';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(feature);
        return acc;
    }, {} as Record<string, FeatureDefinition[]>);

    const filteredCategories = filterCategory === 'all'
        ? FEATURE_CATEGORIES.filter(c => groupedFeatures[c.key]?.length > 0)
        : FEATURE_CATEGORIES.filter(c => c.key === filterCategory && groupedFeatures[c.key]?.length > 0);

    const [showResetModal, setShowResetModal] = useState(false);

    const handleResetDefaults = async () => {
        try {
            setLoading(true);
            await featuresApi.resetDefaults();
            await loadFeatures();
            setShowResetModal(false);
        } catch (err: any) {
            setError('Unable to reset features. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const ConfirmResetModal = () => {
        if (!showResetModal) return null;

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 m-4 animate-in fade-in zoom-in-95 duration-200">
                    <div className="flex items-center gap-3 text-amber-600 mb-4">
                        <div className="p-2 bg-amber-100 rounded-lg">
                            <AlertTriangle size={24} />
                        </div>
                        <h3 className="text-lg font-semibold text-neutral-900">Reset Feature Defaults</h3>
                    </div>

                    <p className="text-neutral-600 mb-6">
                        This will force a re-seed of all default feature definitions.
                        Custom feature values may be overwritten if they conflict with updates.
                        Are you sure you want to continue?
                    </p>
                    <div className="flex items-center justify-end gap-3">
                        <button
                            onClick={() => setShowResetModal(false)}
                            className="px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleResetDefaults}
                            className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors flex items-center gap-2"
                        >
                            <RefreshCw size={16} />
                            Reset Defaults
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="h-full flex flex-col p-1 animate-fade-in max-w-6xl mx-auto w-full">
            <div className="flex-1 bg-white border border-neutral-200 rounded-lg overflow-hidden flex flex-col shadow-sm">

                {/* Toolbar */}
                <div className="flex items-center justify-between p-2 border-b border-neutral-200 bg-neutral-50/50">
                    <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider px-2">
                        Feature Registry
                    </span>
                    <div className="flex items-center gap-2">
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="text-xs border border-neutral-200 rounded-md bg-white text-neutral-600 py-1 pl-2 pr-6 focus:ring-0 focus:border-neutral-300"
                        >
                            <option value="all">All Categories</option>
                            {FEATURE_CATEGORIES.map(cat => (
                                <option key={cat.key} value={cat.key}>{cat.label}</option>
                            ))}
                        </select>
                        <button
                            onClick={() => setShowResetModal(true)}
                            className="btn-secondary !px-3 !py-1.5 !text-xs flex items-center gap-1.5"
                            title="Reset Defaults"
                        >
                            <RefreshCw size={12} />
                        </button>
                        <button
                            onClick={startCreate}
                            className="btn-primary !px-3 !py-1.5 !text-xs flex items-center gap-1.5"
                        >
                            <Plus size={12} />
                            New Feature
                        </button>
                    </div>
                </div>

                {/* Error message */}
                {error && (
                    <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-red-600 text-sm flex items-center justify-between">
                        <span>{error}</span>
                        <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                            <X size={14} />
                        </button>
                    </div>
                )}

                {/* Create Form */}
                {isCreating && (
                    <div className="p-4 border-b border-neutral-200 bg-blue-50/30">
                        <h3 className="text-sm font-semibold text-neutral-700 mb-3">New Feature Definition</h3>
                        <FeatureForm
                            formData={formData}
                            setFormData={setFormData}
                            onSave={handleSave}
                            onCancel={cancelEdit}
                            saving={saving}
                            isNew={true}
                        />
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-12 flex flex-col items-center justify-center text-center text-neutral-500 h-full">
                            <p className="text-sm">Loading features...</p>
                        </div>
                    ) : features.length === 0 ? (
                        <div className="p-12 flex flex-col items-center justify-center text-center text-neutral-500 h-full">
                            <Settings size={32} className="mb-3 text-neutral-300" />
                            <p className="text-sm">No features defined yet</p>
                            <button
                                onClick={startCreate}
                                className="mt-4 text-xs text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Create your first feature
                            </button>
                        </div>
                    ) : (
                        <div className="divide-y divide-neutral-100">
                            {filteredCategories.map(category => {
                                const Icon = CategoryIcon[category.key] || Settings;
                                const categoryFeatures = groupedFeatures[category.key] || [];

                                return (
                                    <div key={category.key} className="py-4">
                                        {/* Category Header */}
                                        <div className="px-4 flex items-center gap-2 mb-3">
                                            <Icon size={16} className="text-neutral-400" />
                                            <h3 className="text-sm font-semibold text-neutral-700">{category.label}</h3>
                                            <span className="text-xs text-neutral-400">({categoryFeatures.length})</span>
                                        </div>

                                        {/* Features in category */}
                                        <div className="px-2">
                                            {categoryFeatures.map(feature => {
                                                const TypeIconComponent = TypeIcon[feature.valueType] || Hash;
                                                const isEditing = editingId === feature.id;

                                                return (
                                                    <div
                                                        key={feature.id}
                                                        className={`mx-2 mb-2 rounded-lg border transition-all ${isEditing
                                                            ? 'border-blue-300 bg-blue-50/30'
                                                            : 'border-neutral-200 bg-white hover:border-neutral-300'
                                                            }`}
                                                    >
                                                        {isEditing ? (
                                                            <div className="p-4">
                                                                <FeatureForm
                                                                    formData={formData}
                                                                    setFormData={setFormData}
                                                                    onSave={handleSave}
                                                                    onCancel={cancelEdit}
                                                                    saving={saving}
                                                                    isNew={false}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div className="p-3 flex items-center justify-between">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-8 h-8 rounded-md bg-neutral-100 flex items-center justify-center">
                                                                        <TypeIconComponent size={14} className="text-neutral-500" />
                                                                    </div>
                                                                    <div>
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="font-medium text-sm text-neutral-900">{feature.name}</span>
                                                                            <code className="text-xs text-neutral-400 bg-neutral-100 px-1.5 py-0.5 rounded">
                                                                                {feature.key}
                                                                            </code>
                                                                        </div>
                                                                        {feature.description && (
                                                                            <p className="text-xs text-neutral-500 mt-0.5">{feature.description}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-4">
                                                                    <div className="text-right">
                                                                        <div className="text-xs text-neutral-500">Default</div>
                                                                        <div className="text-sm font-medium text-neutral-700">
                                                                            {formatDefaultValue(parseDefaultValue(feature), feature.valueType)}
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center gap-1">
                                                                        <button
                                                                            onClick={() => startEdit(feature)}
                                                                            className="p-1.5 rounded-md hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600 transition-colors"
                                                                        >
                                                                            <Edit2 size={14} />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleDelete(feature.id)}
                                                                            className="p-1.5 rounded-md hover:bg-red-50 text-neutral-400 hover:text-red-600 transition-colors"
                                                                        >
                                                                            <Trash2 size={14} />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Reset Modal */}
            {showResetModal && <ConfirmResetModal />}
        </div>
    );
};

// Feature Form Component
interface FeatureFormProps {
    formData: FeatureFormData;
    setFormData: React.Dispatch<React.SetStateAction<FeatureFormData>>;
    onSave: () => void;
    onCancel: () => void;
    saving: boolean;
    isNew: boolean;
}

const FeatureForm: React.FC<FeatureFormProps> = ({
    formData,
    setFormData,
    onSave,
    onCancel,
    saving,
    isNew
}) => {
    const renderDefaultValueInput = () => {
        switch (formData.valueType) {
            case 'BOOLEAN':
                return (
                    <select
                        value={formData.defaultValue ? 'true' : 'false'}
                        onChange={(e) => setFormData(prev => ({ ...prev, defaultValue: e.target.value === 'true' }))}
                        className="w-full text-sm border border-neutral-300 rounded-md px-2 py-1.5 focus:ring-1 focus:ring-accent-primary focus:border-accent-primary"
                    >
                        <option value="false">No / Disabled</option>
                        <option value="true">Yes / Enabled</option>
                    </select>
                );
            case 'INTEGER':
                return (
                    <input
                        type="number"
                        value={formData.defaultValue}
                        onChange={(e) => setFormData(prev => ({ ...prev, defaultValue: parseInt(e.target.value) || 0 }))}
                        className="w-full text-sm border border-neutral-300 rounded-md px-2 py-1.5 focus:ring-1 focus:ring-accent-primary focus:border-accent-primary"
                        placeholder="Enter number (-1 for unlimited)"
                    />
                );
            case 'STRING_ARRAY':
                return (
                    <input
                        type="text"
                        value={Array.isArray(formData.defaultValue) ? formData.defaultValue.join(', ') : ''}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            defaultValue: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                        }))}
                        className="w-full text-sm border border-neutral-300 rounded-md px-2 py-1.5 focus:ring-1 focus:ring-accent-primary focus:border-accent-primary"
                        placeholder="value1, value2, value3 (use * for all)"
                    />
                );
            default:
                return (
                    <input
                        type="number"
                        value={formData.defaultValue}
                        onChange={(e) => setFormData(prev => ({ ...prev, defaultValue: parseInt(e.target.value) || 0 }))}
                        className="w-full text-sm border border-neutral-300 rounded-md px-2 py-1.5 focus:ring-1 focus:ring-accent-primary focus:border-accent-primary"
                        placeholder="Days (-1 for unlimited)"
                    />
                );
        }
    };

    return (
        <div className="grid grid-cols-2 gap-4">
            {/* Key (only editable for new) */}
            <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Feature Key</label>
                <input
                    type="text"
                    value={formData.key}
                    onChange={(e) => setFormData(prev => ({ ...prev, key: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                    disabled={!isNew}
                    className="w-full text-sm border border-neutral-300 rounded-md px-2 py-1.5 focus:ring-1 focus:ring-accent-primary focus:border-accent-primary disabled:bg-neutral-100 disabled:text-neutral-500"
                    placeholder="e.g., max_exports"
                />
            </div>

            {/* Name */}
            <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Display Name</label>
                <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full text-sm border border-neutral-300 rounded-md px-2 py-1.5 focus:ring-1 focus:ring-accent-primary focus:border-accent-primary"
                    placeholder="e.g., Maximum Exports"
                />
            </div>

            {/* Category */}
            <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Category</label>
                <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full text-sm border border-neutral-300 rounded-md px-2 py-1.5 focus:ring-1 focus:ring-accent-primary focus:border-accent-primary"
                >
                    {FEATURE_CATEGORIES.map(cat => (
                        <option key={cat.key} value={cat.key}>{cat.label}</option>
                    ))}
                </select>
            </div>

            {/* Type */}
            <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Value Type</label>
                <select
                    value={formData.valueType}
                    onChange={(e) => {
                        const type = e.target.value;
                        let defaultVal: any = false;
                        if (type === 'INTEGER' || type === 'DATE_RANGE_DAYS') defaultVal = 0;
                        if (type === 'STRING_ARRAY') defaultVal = [];
                        setFormData(prev => ({ ...prev, valueType: type, defaultValue: defaultVal }));
                    }}
                    className="w-full text-sm border border-neutral-300 rounded-md px-2 py-1.5 focus:ring-1 focus:ring-accent-primary focus:border-accent-primary"
                >
                    <option value="BOOLEAN">Boolean (Yes/No)</option>
                    <option value="INTEGER">Integer (Number)</option>
                    <option value="STRING_ARRAY">String Array (List)</option>
                    <option value="DATE_RANGE_DAYS">Date Range (Days)</option>
                </select>
            </div>

            {/* Default Value */}
            <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Default Value</label>
                {renderDefaultValueInput()}
            </div>

            {/* Usage Model (optional) */}
            <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Usage Model (optional)</label>
                <input
                    type="text"
                    value={formData.usageModel}
                    onChange={(e) => setFormData(prev => ({ ...prev, usageModel: e.target.value }))}
                    className="w-full text-sm border border-neutral-300 rounded-md px-2 py-1.5 focus:ring-1 focus:ring-accent-primary focus:border-accent-primary"
                    placeholder="e.g., Board, Channel"
                />
            </div>

            {/* Description */}
            <div className="col-span-2">
                <label className="block text-xs font-medium text-neutral-600 mb-1">Description</label>
                <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full text-sm border border-neutral-300 rounded-md px-2 py-1.5 focus:ring-1 focus:ring-accent-primary focus:border-accent-primary"
                    placeholder="Brief description of what this feature controls"
                />
            </div>

            {/* Actions */}
            <div className="col-span-2 flex items-center justify-end gap-2 pt-2">
                <button
                    onClick={onCancel}
                    disabled={saving}
                    className="btn-secondary !px-3 !py-1.5 !text-xs"
                >
                    Cancel
                </button>
                <button
                    onClick={onSave}
                    disabled={saving || !formData.key || !formData.name}
                    className="btn-primary !px-3 !py-1.5 !text-xs flex items-center gap-1.5"
                >
                    <Save size={12} />
                    {saving ? 'Saving...' : 'Save Feature'}
                </button>
            </div>
        </div>
    );
};

export default FeatureRegistryPage;
